import { Box } from '@lifeomic/chroma-react/components/Box';
import { ButtonFilePicker } from '@lifeomic/chroma-react/components/ButtonFilePicker';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { IconButtonLink } from '@lifeomic/chroma-react/components/IconButtonLink';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { Download, Eye, FileText, PlusCircle } from '@lifeomic/chromicons';
import clsx from 'clsx';
import { FieldProps } from 'formik';
import _get from 'lodash/get';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import {
  useActiveProjectCohortId,
  useActiveProjectId,
  usePrevious,
} from '../../../../hooks';
import useService from '../../../../hooks/useService';
import {
  getReactNativeWebView,
  sendMessage,
} from '../../../../util/reactNative';
import { getTestProps } from '../../../../util/testProps';
import urls from '../../../../util/urls';
import SurveyFilesService from '../../SurveyFilesService';
import SurveyItem from '../../util/SurveyItem';
import getSurveyItemTestId from '../../util/getSurveyItemTestId';
import {
  withIsReadOnly,
  WithIsReadOnlyInjectedProps,
  withSurveyResponseId,
  WithSurveyResponseIdInjectedProps,
} from '../Contexts';
import SurveyField from '../SurveyField';
import SurveyItemContainer from '../SurveyItemContainer';
import withCheckForEnablement, {
  WithCheckForEnablementInjectedProps,
} from '../hoc/withCheckForEnablement';
import withValidation, {
  WithValidationInjectedProps,
} from '../hoc/withValidation';

export const testIds = {
  attachment: (linkId: string) => getSurveyItemTestId(linkId),
};

function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

const surveyFilesService = new SurveyFilesService();

export const useStyles = makeStyles((theme: Theme) => ({
  labelText: {
    color: theme.palette.text.secondary,
  },
  filePreviewWrapper: {
    margin: '10px 0',
  },
  filePreview: {
    maxWidth: '100%',
    maxHeight: 400,
  },
  disclaimerText: {
    marginTop: theme.spacing(1),
  },
  errorText: {
    marginTop: theme.spacing(1),
    color: theme.palette.error.main,
  },
  errorLabel: {
    color: theme.palette.error.main,
  },
}));

const messages = defineMessages({
  attachmentPreview: {
    id: 'attachment.attachmentPreview',
    defaultMessage: 'Attachment preview',
  },
  previewFile: {
    id: 'attachment.previewFile',
    defaultMessage: 'Preview file in a new tab',
  },
  downloadFile: {
    id: 'attachment.downloadFile',
    defaultMessage: 'Download file',
  },
});

export enum FileUploadState {
  NotStarted = 'NOT_STARTED',
  Uploading = 'UPLOADING',
  Failed = 'FAILED',
  Completed = 'COMPLETED',
}

export interface FileUploadResult {
  fileUploadState: FileUploadState;
  fileUploadError?: Error;
  fileId?: string;
  name?: string;
  fileContentType?: string;
}

export interface FieldComponentOwnProps {
  isReadOnly: boolean;
  classes: { [key: string]: string };
  allowedExtensions: string;
  surveyItem: SurveyItem;
  shouldShowFilePreview?: boolean;
  attachButtonTitle?: string;
  fileProcessor?: (file: File) => Promise<File | Blob>;
}

export type FieldComponentProps = FieldComponentOwnProps &
  FieldProps<any> &
  WithSurveyResponseIdInjectedProps &
  WithValidationInjectedProps;

const rethrowOptions = {
  rethrowErrors: true,
};

const FieldComponent = (props: FieldComponentProps) => {
  const [filePreview, setFilePreview] = useState<
    | {
        type: 'image' | 'pdf';
        downloadLink?: string;
        previewLink?: string;
        title: string;
      }
    | undefined
  >();
  const [uploadStatus, setUploadStatus] = useState({
    fileUploadState: FileUploadState.NotStarted,
  } as FileUploadResult);
  const [createSurveyFile] = useService(
    surveyFilesService.createSurveyFile,
    rethrowOptions,
  );
  const [uploadSurveyFile] = useService(
    surveyFilesService.uploadSurveyFile,
    rethrowOptions,
  );
  const [getDownloadLink, getDownloadLinkState] = useService(
    surveyFilesService.getDownloadLink,
    rethrowOptions,
  );
  const pendingFileUpload = useRef<File>();

  const activeProjectId = useActiveProjectId();
  const cohort = useActiveProjectCohortId();
  const previousUploadStatus = usePrevious(uploadStatus);
  const classes = useStyles(props);
  const { formatMessage } = useIntl();

  const isUploading =
    uploadStatus.fileUploadState === FileUploadState.Uploading;
  const isDownloading = getDownloadLinkState.isFetching;

  const upsertFile = useCallback(
    async (e: any): Promise<any> => {
      const isResolvePending = e.isResolvePending;
      pendingFileUpload.current = undefined;
      let file = e.target.files && e.target.files[0];

      if (!file) {
        return null;
      }

      const fileType = file?.type;
      const fileName = file?.name;

      try {
        if (!isResolvePending) {
          setUploadStatus({
            fileUploadState: FileUploadState.Uploading,
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            fileUploadError: null,
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            fileId: null,
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            name: null,
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            fileContentType: null,
          });
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          setFilePreview(null);
        }

        if (props.surveyResponseId) {
          // Here consuming components can manipulate the file pre upload / preview.
          if (props.fileProcessor) {
            file = await props.fileProcessor(file);
          }

          const createFileResult = await createSurveyFile({
            projectId: activeProjectId,
            responseId: props.surveyResponseId,
            linkId: props.surveyItem.link,
            file,
            cohort,
          });
          const uploadUrl = _get(createFileResult, 'data.uploadUrl');
          const contentType = _get(createFileResult, 'data.contentType');
          await uploadSurveyFile(uploadUrl, contentType, file);
          const fileId = _get(createFileResult, 'data.id');

          setUploadStatus({
            fileUploadState: FileUploadState.Completed,
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            fileUploadError: null,
            fileId: fileId,
            name: fileName,
            fileContentType: contentType,
          });
        } else {
          pendingFileUpload.current = e.target.files[0];
          /*
          survey is in survey preview mode and files should not actually be uploaded.
          Try to mimic the experience as much as possible to give an accurate representation in the preview
        */
          await sleep(500);
          setUploadStatus({
            fileUploadState: FileUploadState.Completed,
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
            fileUploadError: null,
            // @ts-expect-error This is not a real file so no file id is available
            fileId: null,
            name: fileName,
            fileContentType: file?.type || 'image/jpeg',
          });
        }
        if (props.shouldShowFilePreview) {
          let downloadLink: string | undefined = URL.createObjectURL(file);

          if (fileType === 'application/pdf') {
            downloadLink = undefined; // Don't show download during upload
          }

          setFilePreview({
            type: fileType === 'application/pdf' ? 'pdf' : 'image',
            downloadLink,
            title: fileName,
          });
        }
      } catch (error) {
        console.error('Error occurred uploading survey file', error);
        setUploadStatus({
          fileUploadState: FileUploadState.Failed,
          fileUploadError: error,
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          fileId: null,
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          name: null,
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          fileContentType: null,
        });
      }
    },
    [props, uploadSurveyFile, createSurveyFile, activeProjectId, cohort],
  );

  useEffect(() => {
    if (props.surveyResponseId && pendingFileUpload.current) {
      upsertFile({
        target: { files: [pendingFileUpload.current] },
        isResolvePending: true,
      });
    }
  }, [props.surveyResponseId, upsertFile]);

  const getButtonTitle = () => {
    if (filePreview) {
      return (
        <FormattedMessage
          id="surveys.attachment.updateFile"
          defaultMessage="Update"
        />
      );
    } else {
      return (
        props.attachButtonTitle || (
          <FormattedMessage
            id="surveys.attachment.addFile"
            defaultMessage="File"
          />
        )
      );
    }
  };

  useEffect(() => {
    if (!previousUploadStatus || !uploadStatus) {
      return;
    }

    const oldFileId = previousUploadStatus.fileId;
    const fileId = uploadStatus.fileId;
    const fileIdChanged = oldFileId !== fileId;

    // If the file was just uploaded or the fileId has changed, the form
    // value needs to be updated to reference the latest attachment url.
    if (
      previousUploadStatus.fileUploadState === FileUploadState.Uploading ||
      fileIdChanged
    ) {
      if (uploadStatus.fileUploadState === FileUploadState.Completed) {
        const value = {
          url: (fileId && urls.api.files.files({}, fileId)) || '',
          contentType: uploadStatus.fileContentType || undefined, // Blob sometimes gives an empty string
          title: uploadStatus.name,
        };
        props.form.setFieldValue(props.field.name, value);
      } else if (uploadStatus.fileUploadState === FileUploadState.Failed) {
        props.form.setFieldValue(props.field.name, null);
      }
    }
  }, [props.field.name, props.form, uploadStatus, previousUploadStatus]);

  useEffect(() => {
    const { shouldShowFilePreview, surveyResponseId, surveyItem } = props;

    async function downloadFilePreview(fileId: string) {
      try {
        const downloadLinkResult = await getDownloadLink(
          activeProjectId,
          surveyResponseId,
          surveyItem.link,
          fileId,
          true, // include content disposition so the file can be downloaded
          cohort,
        );
        let previewLink: string | undefined;

        const contentType = _get(downloadLinkResult, 'data.contentType');
        if (contentType === 'application/pdf') {
          const previewLinkResult = await getDownloadLink(
            activeProjectId,
            surveyResponseId,
            surveyItem.link,
            fileId,
            false, // don't include content disposition so the file can be viewed in the browser
            cohort,
          );
          previewLink = _get(previewLinkResult, 'data.downloadUrl');
        }
        const downloadLink = _get(downloadLinkResult, 'data.downloadUrl');
        const title = _get(props.field.value, 'title');
        setFilePreview({
          type: contentType === 'application/pdf' ? 'pdf' : 'image',
          downloadLink,
          previewLink,
          title,
        });
      } catch (error) {
        console.error('Error occurred downloading survey file preview', error);
      }
    }

    if (shouldShowFilePreview) {
      const fileUrl = _get(props.field.value, 'url');
      if (
        props.surveyResponseId &&
        fileUrl &&
        !filePreview &&
        !getDownloadLinkState.isFetching &&
        !getDownloadLinkState.error &&
        !getDownloadLinkState.response
      ) {
        const fileId = fileUrl.substring(fileUrl.lastIndexOf('/') + 1);
        downloadFilePreview(fileId);
      }
    }
  }, [
    activeProjectId,
    cohort,
    props,
    filePreview,
    getDownloadLink,
    getDownloadLinkState,
  ]);

  return (
    <React.Fragment>
      <div className={classes.filePreviewWrapper}>
        {(isUploading || isDownloading) && <DotLoader size={1} />}
        {!isUploading &&
          filePreview &&
          (filePreview.type === 'pdf' ? (
            <Box dir="row">
              <FileText />

              <Text>
                {filePreview.title || (
                  <FormattedMessage
                    id="surveys.attachment.defaultPdfTitle"
                    defaultMessage="PDF document"
                  />
                )}
              </Text>
              {filePreview.downloadLink && (
                <Tooltip title={formatMessage(messages.downloadFile)}>
                  <IconButtonLink
                    aria-label={formatMessage(messages.downloadFile)}
                    icon={Download}
                    to={filePreview.downloadLink}
                    paddingTop={0}
                    paddingBottom={0}
                    paddingLeft={1}
                    paddingRight={1}
                    target="_blank"
                    onClick={e => {
                      if (getReactNativeWebView()) {
                        e.preventDefault();
                        sendMessage(
                          JSON.stringify({
                            type: 'fileDownload',
                            url: filePreview.downloadLink,
                            title: filePreview.title,
                          }),
                        );
                      }
                    }}
                  />
                </Tooltip>
              )}
              {filePreview.previewLink && (
                <Tooltip title={formatMessage(messages.previewFile)}>
                  <IconButtonLink
                    aria-label={formatMessage(messages.previewFile)}
                    icon={Eye}
                    to={filePreview.previewLink}
                    paddingTop={0}
                    paddingBottom={0}
                    paddingLeft={1}
                    paddingRight={1}
                    target="_blank"
                    onClick={e => {
                      if (getReactNativeWebView()) {
                        e.preventDefault();
                        sendMessage(
                          JSON.stringify({
                            type: 'filePreview',
                            url: filePreview.previewLink,
                            title: filePreview.title,
                          }),
                        );
                      }
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          ) : (
            <img
              src={filePreview.downloadLink}
              alt={formatMessage(messages.attachmentPreview)}
              className={classes.filePreview}
            />
          ))}
        {uploadStatus.fileUploadError && (
          <FormattedMessage
            id="surveys.attachment.fileUploadError"
            defaultMessage="An error occurred uploading the file. Please try again"
          />
        )}
        {!isUploading &&
          !isDownloading &&
          !filePreview &&
          getDownloadLinkState.error && (
            <FormattedMessage
              id="surveys.attachment.filePreviewDownloadError"
              defaultMessage="An error occurred downloading the file. Please try again"
            />
          )}
      </div>
      <ButtonFilePicker
        variant="outlined"
        icon={PlusCircle}
        accept={props.allowedExtensions}
        onChange={upsertFile}
        disabled={isUploading || props.isReadOnly}
        {...getTestProps(testIds.attachment(props.surveyItem.link))}
      >
        {getButtonTitle()}
      </ButtonFilePicker>
      {props.shouldShowError && (
        <Text size="caption" className={classes.errorText}>
          {props.error}
        </Text>
      )}
    </React.Fragment>
  );
};

export interface AttachmentDispatchProps {
  upsertPatientFile?: (
    projectId: string,
    file: File,
    correlationId: string,
  ) => any;
  getPatientFileDownloadUrl?: (fileId: string) => any;
}

export interface AttachmentOwnProps {
  surveyItem: SurveyItem;
  allowedExtensions: string;
  shouldShowFilePreview?: boolean;
  attachButtonTitle?: string;
  fileProcessor?: (file: File) => Promise<File | Blob>;
}

export type AttachmentProps = AttachmentOwnProps &
  AttachmentDispatchProps &
  WithCheckForEnablementInjectedProps &
  WithIsReadOnlyInjectedProps &
  WithSurveyResponseIdInjectedProps &
  WithValidationInjectedProps;

const Attachment = (props: AttachmentProps) => {
  const classes = useStyles(props);
  const {
    surveyItem,
    isEnabled,
    isReadOnly,
    allowedExtensions,
    shouldShowFilePreview,
    upsertPatientFile,
    surveyResponseId,
    validate,
    error,
    shouldShowError,
    attachButtonTitle,
    fileProcessor,
  } = props;
  const getQuestionText = () => {
    const {
      surveyItem: { prefix, text, label, required },
    } = props;

    let headerText = '';
    if (prefix) {
      headerText = `${required ? '* ' : ''}(${prefix}) ${text}`;
    } else {
      headerText = label || '';
    }

    if (!headerText) return null;

    return (
      <Text
        size="body"
        className={clsx(
          classes.labelText,
          shouldShowError && classes.errorLabel,
        )}
      >
        {headerText}
      </Text>
    );
  };

  const activeProjectId = useActiveProjectId();

  if (!isEnabled) return null;

  return (
    <SurveyItemContainer surveyItem={surveyItem}>
      <div>
        {getQuestionText()}
        <SurveyField
          responseAnswerType={surveyItem.responseValueType}
          name={surveyItem.link}
          component={FieldComponent}
          surveyItem={surveyItem}
          allowedExtensions={allowedExtensions}
          shouldShowFilePreview={shouldShowFilePreview}
          isReadOnly={isReadOnly}
          upsertPatientFile={upsertPatientFile}
          activeProjectId={activeProjectId}
          surveyResponseId={surveyResponseId}
          attachButtonTitle={attachButtonTitle}
          fileProcessor={fileProcessor}
          classes={classes}
          validate={validate}
          error={error}
          shouldShowError={shouldShowError}
        />
      </div>
    </SurveyItemContainer>
  );
};

export default withCheckForEnablement(
  withIsReadOnly(withSurveyResponseId(withValidation(Attachment))),
);
